import { LoadingButton } from '@mui/lab'
import { Autocomplete, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, InputAdornment, TextField } from '@mui/material'
import { GreenText, LabelField } from 'components'
import { Chip } from 'components/chips'
import { DateField } from 'components/fields'
import * as controllers from 'controllers'
import { CompanyDto, CompanyTransporterInformationDto, InboundTripDto } from 'dtos'
import { inboundTripValidationSchema } from 'dtos/inboundTripDto'
import { Formik } from 'formik'
import { useLoadingState } from 'hooks'
import { AUTOCOMPLETE_PAGESIZE, DEBOUNCE_DELAY, Junction } from 'localConstants'
import { useState } from 'react'
import { useDebounce } from 'utils'

interface InboundTripsAddEditDialogProps {
  initialValues: InboundTripDto
  loading: boolean
  open: boolean
  onClose: () => void
  onSave: (values: InboundTripDto) => void
}

export default function InboundTripsAddEditDialog({ initialValues, onClose, onSave, loading, open }: InboundTripsAddEditDialogProps) {
  const [loadingState, setLoadingState] = useLoadingState({
    isGettingCompanyTransporterInformation: false,
    isGettingInboundTransporterCompanies: false,
    isGettingInboundTransporterCompanyAddresses: false
  })
  const [inboundTransporterCompanies, setInboundTransporterCompanies] = useState<CompanyDto[]>([])
  const [inboundTransporterCompanyAddressEmails, setInboundTransporterCompanyAddressEmails] = useState<string[]>([])
  const [companyTransporterInformation, setCompanyTransporterInformation] = useState<CompanyTransporterInformationDto | null>(null)

  const getInboundTransporterCompaniesAutocomplete = useDebounce((search: string) => {
    setLoadingState('isGettingInboundTransporterCompanies', true)
    controllers
      .getCompanies({
        junction: Junction.AND,
        order: 'asc',
        orderBy: 'name',
        page: 0,
        pageSize: AUTOCOMPLETE_PAGESIZE,
        search,
        statuses: ['isActive', 'isInboundTransporterCompany']
      })
      .then(response => {
        setInboundTransporterCompanies(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingInboundTransporterCompanies', false)
      })
  }, DEBOUNCE_DELAY)

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
        validateOnBlur={true}
        validateOnChange={true}
        validationSchema={inboundTripValidationSchema}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>
                    {initialValues.inboundTripGuid ? 'Edit' : 'Add'} Inbound Trip {initialValues.isLocked && <Chip variant='red'>Locked</Chip>}
                  </DialogTitle>

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting || initialValues.isLocked}
                      error={Boolean(touched.inboundTripNumber && errors.inboundTripNumber)}
                      helperText={touched.inboundTripNumber && errors.inboundTripNumber}
                      label='Inbound Trip #'
                      name='inboundTripNumber'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.inboundTripNumber}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={loading || isSubmitting || initialValues.isLocked}
                      disableListWrap
                      getOptionLabel={company => company.name!}
                      noOptionsText={
                        loadingState.isGettingInboundTransporterCompanies
                          ? 'Searching Inbound Transporter Companies...'
                          : 'No Inbound Transporter Companies. Start typing to search for Inbound Transporter Companies...'
                      }
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('inboundTransporterCompany', value)
                        setFieldValue('inboundTransporterCompanySCAC', undefined)
                        setFieldValue('inboundTransporterCompanyEmail', undefined)
                        setCompanyTransporterInformation(null)

                        if (value?.companyGuid) {
                          setLoadingState('isGettingInboundTransporterCompanyAddresses', true)
                          controllers
                            .getCompanyAddresses(value.companyGuid, {
                              junction: Junction.AND,
                              page: 0,
                              pageSize: AUTOCOMPLETE_PAGESIZE,
                              statuses: ['isActive', 'isInboundTransporterAddress']
                            })
                            .then(response => {
                              setInboundTransporterCompanyAddressEmails(
                                response.value.filter(companyAddress => companyAddress.email).map(companyAddress => companyAddress.email!)
                              )
                            })
                            .finally(() => {
                              setLoadingState('isGettingInboundTransporterCompanyAddresses', false)
                            })

                          setLoadingState('isGettingCompanyTransporterInformation', true)
                          controllers
                            .getCompanyTransporterInformation(value.companyGuid)
                            .then(response => {
                              setCompanyTransporterInformation(response.value)
                            })
                            .finally(() => {
                              setLoadingState('isGettingCompanyTransporterInformation', false)
                            })
                        }
                      }}
                      onInputChange={(_e, value, _reason) => {
                        getInboundTransporterCompaniesAutocomplete(value)
                      }}
                      options={inboundTransporterCompanies.map(inboundTransporterCompany => ({
                        companyGuid: inboundTransporterCompany.companyGuid,
                        name: inboundTransporterCompany.name
                      }))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.inboundTransporterCompany && errors.inboundTransporterCompany)}
                          helperText={touched.inboundTransporterCompany && errors.inboundTransporterCompany}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingState.isGettingInboundTransporterCompanies && (
                                  <InputAdornment position='end'>
                                    <CircularProgress size={24} />
                                  </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                          label='Inbound Transporter Company'
                        />
                      )}
                      value={
                        values.inboundTransporterCompany
                          ? {
                              companyGuid: values.inboundTransporterCompany.companyGuid,
                              name: values.inboundTransporterCompany.name
                            }
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField
                      isLoading={loadingState.isGettingCompanyTransporterInformation}
                      label='SCAC'
                      value={companyTransporterInformation?.transporterSCAC ?? values.inboundTransporterCompanySCAC ?? ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={loading || isSubmitting || initialValues.isLocked}
                      disableListWrap
                      noOptionsText={
                        loadingState.isGettingInboundTransporterCompanyAddresses
                          ? 'Searching Inbound Transporter Company Address Emails...'
                          : 'No Inbound Transporter Company Address Emails.'
                      }
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('inboundTransporterCompanyEmail', value)
                      }}
                      options={inboundTransporterCompanyAddressEmails}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.inboundTransporterCompanyEmail && errors.inboundTransporterCompanyEmail)}
                          helperText={touched.inboundTransporterCompanyEmail && errors.inboundTransporterCompanyEmail}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingState.isGettingInboundTransporterCompanyAddresses && (
                                  <InputAdornment position='end'>
                                    <CircularProgress size={24} />
                                  </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                          label='Inbound Transporter Company Email'
                        />
                      )}
                      value={values.inboundTransporterCompanyEmail || undefined}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.entryNumber && errors.entryNumber)}
                      helperText={touched.entryNumber && errors.entryNumber}
                      label='Entry #'
                      name='entryNumber'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.entryNumber}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <DateField
                      clearable
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.entryDate && errors.entryDate)}
                      helperText={touched.entryDate && errors.entryDate}
                      label='Entry Date'
                      name='entryDate'
                      onChange={value => setFieldValue('entryDate', value)}
                      value={values.entryDate || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**SCAC:**

A free-text field with a 25-character limit, allowing users to input the SCAC

**DOT #:**

A free-text field with a 25-character limit, allowing users to input the DOT
Number

**CANCEL Button:**

Clicking the CANCEL button will close the dialog and not save any changes

**SAVE Button:**

Clicking the SAVE button will save any changes.

`}
                    </GreenText>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button variant='text' color='inherit' onClick={onClose}>
                  CANCEL
                </Button>

                <LoadingButton loading={loading || isSubmitting} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
